import classNames from 'classnames';
import { PureComponent } from 'react';

import Player, { AspectRatioWrapper } from 'my-components/VideoPlayer';

import Typography from '@mui/material/Typography';

import warning from 'images/icons/in-development.svg';

export default class ActivityVideoPlayer extends PureComponent {
  componentDidMount() {
    const { autoplay, onTogglePlaying, reviewMode } = this.props;
    if (autoplay && onTogglePlaying) {
      // video animation duration = 2500ms;
      this.playTimeout = setTimeout(onTogglePlaying, reviewMode ? 500 : 2500);
    }
  }
  componentWillUnmount() {
    if (this.playTimeout) clearTimeout(this.playTimeout);
    this.savePlayedProgress();
  }
  savePlayedProgress() {
    if (this.currentProgress) {
      const { onSaveProgress } = this.props;
      if (!onSaveProgress) return;
      onSaveProgress({ played: this.currentProgress.played });
      this.currentProgress = null;
    }
  }

  togglePlaying = () => {
    const { onTogglePlaying, playing } = this.props;
    if (playing) this.savePlayedProgress();
    onTogglePlaying?.();
  };

  handleProgressChange = progress => {
    const { onCompleted, onProgress, onSaveProgress, onVideoEnded } = this.props;

    if (onSaveProgress) {
      this.prevProgress = this.currentProgress;
      this.currentProgress = progress;
      if (this.prevProgress && Math.abs(this.prevProgress.playedSeconds - progress.playedSeconds) > 3) {
        this.savePlayedProgress();
      }
    }
    if (!this.isCompletedUpdated && progress.played > 0.8 && onCompleted) {
      this.isCompletedUpdated = true;
      onCompleted();
    }
    onProgress?.(progress);
    if (progress.played >= 1) {
      // this.togglePlaying(); // auto called by player when video ends (and pauses)
      if (onVideoEnded) {
        setTimeout(onVideoEnded, 0);
      }
    }
  };

  handlePreviewProgressChange = progress => {
    const { onMaxProgressReached, onProgress } = this.props;
    onProgress?.(progress);
    if (progress.played >= 1) {
      // this.togglePlaying(); // auto called by player when video ends (and pauses)
      if (onMaxProgressReached) {
        setTimeout(onMaxProgressReached, 0);
      }
    }
  };

  render() {
    const {
      elements,
      enableTour,
      eventTrackingProperties,
      extraControls,
      fetching,
      maxProgressPct,
      maxProgressSecs,
      onMaxProgressReached,
      onSaveProgress,
      onToggleFullscreen,
      onTogglePlaying,
      playing,
      poster,
      progress,
      video,
      videoDrmId,
    } = this.props;
    const duration = video?.duration || 0;
    let startPosition = (progress?.played || 0) * duration;
    if (duration - startPosition < 1) startPosition = 0;
    if (maxProgressPct && startPosition + 1 > (duration * maxProgressPct) / 100) startPosition = 0;
    if (maxProgressSecs && startPosition + 1 > maxProgressSecs) startPosition = 0;

    if (video) {
      const playerProps = {
        css: playerCss,
        containerClassName: 'AVP-playerContainer',
        enableTour,
        extraControls,
        playing,
        poster,
        onToggleFullscreen,
        trackingProps: {
          video_id: video.id,
          ...eventTrackingProperties,
        },
      };

      if ((maxProgressPct || maxProgressSecs) && video.urls?.drm_protected) {
        if (video.urls.preview) {
          return (
            <Player
              {...playerProps}
              onProgress={this.handlePreviewProgressChange}
              onTogglePlaying={onTogglePlaying}
              urls={video.urls.preview}
            />
          );
        }
        return (
          <AspectRatioWrapper css={wrapperCss} wrapperClassName="AVP-emptyContentInner">
            <img alt="warning" src={warning} />
            <Typography variant="h6">No preview available for this video</Typography>
          </AspectRatioWrapper>
        );
      }
      return (
        <Player
          {...playerProps}
          elements={elements}
          maxProgressPct={maxProgressPct}
          maxProgressSecs={maxProgressSecs}
          onMaxProgressReached={onMaxProgressReached}
          onProgress={this.handleProgressChange}
          onTogglePlaying={onSaveProgress ? this.togglePlaying : onTogglePlaying}
          startPosition={startPosition}
          urls={video.urls}
          videoDrmId={videoDrmId}
        />
      );
    }
    return (
      <AspectRatioWrapper
        css={wrapperCss}
        wrapperClassName={classNames('AVP-emptyContentInner', { 'AVP-emptyContentFetching': fetching })}
      >
        {!fetching && (
          <>
            <img alt="warning" src={warning} />
            <Typography variant="h6">Video not uploaded yet</Typography>
          </>
        )}
      </AspectRatioWrapper>
    );
  }
}

const wrapperCss = ({ palette, spacing }) => ({
  '.AVP-emptyContentInner': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '5%',
    backgroundColor: palette.background.paper,
    '& > img': {
      display: 'block',
      marginBottom: spacing(2),
      width: 48,
      height: 48,
    },
  },
  '.AVP-emptyContentFetching': { backgroundColor: palette.common.black },
});
const playerCss = ({ palette }) => ({
  '.AVP-playerContainer': { backgroundColor: `${palette.background.paper} !important` },
});
